import React from 'react';
import { useTranslation } from 'react-i18next';
import TodoList, { type TodoListItem } from './TodoList';
import type { CompanyTodoId } from '../utils/companyTodos';
import { sortByPriority, type TodoPriority } from '../utils/todoPriority';

export interface CompanyTodoRow {
  id: CompanyTodoId;
  priority: TodoPriority;
  /** Since when the row has waited, for the order within a priority. */
  since?: string | null;
  /** Runs when the item's action button is pressed — navigates or triggers a flow. */
  onAction: () => void;
  /** Distinguishes several rows of one kind, e.g. one per stage to attest. */
  key?: string;
  /** Copy under home.todoNow.items.<textKey> instead of <id>, filled in with `vars`. */
  textKey?: string;
  vars?: Record<string, string | number>;
}

interface CompanyTodoListProps {
  items: CompanyTodoRow[];
  /** Cards shown before "Visa fler". */
  maxVisible?: number;
}

// Feather icon per item — reuses the platform's existing icon vocabulary.
const TODO_ICON: Record<CompanyTodoId, string> = {
  invite: 'user-plus',
  createProject: 'folder-plus',
  kyc: 'shield',
  bankAccount: 'credit-card',
  capabilities: 'check-circle',
  vat: 'file-text',
  attest: 'check-square',
  disputed: 'alert-triangle',
  awaitingSignature: 'edit',
  contractRejected: 'x-circle',
  practicalInfo: 'info',
  addCustomer: 'user-plus',
  customerWaiting: 'clock',
};

// Rows with a project/contract line under the title ("…context" in sv.json).
const WITH_CONTEXT = new Set<string>(['attestMilestone', 'contractReturned', 'contractReturnedComment', 'disputedMilestone', 'underResolutionMilestone', 'conflictMilestone', 'signContract', 'addCustomer', 'customerWaiting', 'customerReminded']);

/** Maps the company's active to-dos (from getActiveCompanyTodos) onto the
 * shared "Att göra nu" list, most important first and, within a priority,
 * what has waited longest first (ClickUp 869fj6nkx). */
export default function CompanyTodoList({ items, maxVisible }: CompanyTodoListProps) {
  const { t } = useTranslation();

  const rows: TodoListItem[] = sortByPriority(items, item => item.priority, item => item.since).map(item => {
    const copy = `home.todoNow.items.${item.textKey ?? item.id}`;
    return {
      key: item.key ?? item.id,
      priority: item.priority,
      icon: TODO_ICON[item.id],
      title: t(`${copy}.title`, item.vars),
      // Which project the row is about, shown even while the explanation is folded.
      context: WITH_CONTEXT.has(item.textKey ?? item.id) ? t(`${copy}.context`, item.vars) : undefined,
      description: t(`${copy}.desc`, item.vars),
      actionLabel: t(`${copy}.action`, item.vars),
      onAction: item.onAction,
      testId: `todo-item-${item.id}`,
    };
  });

  return <TodoList items={rows} maxVisible={maxVisible} emptyTestId="todo-empty" />;
}
